/**
 * Astrally commercial model — single source of truth for products, limits,
 * credit costs and tuning weights.
 *
 * Three layers:
 *  1. BASE  — ₹49 one-time purchase, permanently unlocks the core app,
 *             with 5 LIFETIME outgoing connection requests.
 *  2. PRO   — subscription: fair-use unlimited standard AI chat, full
 *             compatibility-ranked discovery, 20 outgoing requests/day.
 *  3. CREDITS — separate wallet for premium generations (Go Deeper /
 *             Master Reading / regenerations). Purchased credits never
 *             expire; promotional credits may; purchases are consumed after
 *             promotional balances.
 *
 * An Android Play purchase unlocks the SAME account everywhere (web
 * included) because entitlements live server-side per uid, never per device.
 *
 * Every value here is a DEFAULT that the Firestore doc `config/app`
 * (server-written, world-readable) can override at runtime — nothing is
 * hard-coded at call sites. See loadRemoteConfig() consumers.
 */

export type PlanTier = 'none' | 'base' | 'pro';

export interface FairUseLimits {
  /** Max user messages per rolling 10 minutes. */
  messagesPerTenMinutes: number;
  /** Max user messages per rolling hour. */
  messagesPerHour: number;
  /** Internal daily abuse threshold (slowdown beyond this). */
  dailyAbuseThreshold: number;
}

export interface CreditCosts {
  /** Sonnet-class "Go Deeper" conversational answer. */
  deeperAnswer: number;
  /** Sonnet-class long-form stored report. */
  deeperReport: number;
  /** Opus-class Master Reading. */
  masterReading: number;
  /** Regenerating a previously stored report. */
  regeneration: number;
}

export interface CreditPack {
  id: string;
  name: string;
  credits: number;
  price: { inr: string; usd: string };
}

export interface DiscoveryWeights {
  compatibility: number;
  intentMatch: number;
  geo: number;
  activity: number;
  profileQuality: number;
  verification: number;
  proBoost: number;
}

export interface AppConfigValues {
  configVersion: string;
  scoringVersion: string;
  compatibilityVersion: string;
  /** Dimension-score → A/B/C/D labels (legacy grades still used in UI chips). */
  gradeThresholds: { A: number; B: number; C: number };

  base: {
    productId: string;
    priceDisplay: { inr: string; usd: string };
    /** LIFETIME outgoing connection requests — not daily, not monthly. */
    lifetimeOutgoingRequests: number;
    introductoryCredits: number;
    /** Discovery cards per day for Base (limited previews). */
    discoveryDailyCards: number;
    /** Free standard-tier AI messages per day for Base ("trial"). */
    aiTrialMessagesPerDay: number;
  };

  pro: {
    products: { monthly: string; annual: string };
    priceDisplay: { monthlyInr: string; annualInr: string; monthlyUsd: string; annualUsd: string };
    /** Outgoing connection requests per rolling day. */
    dailyOutgoingRequests: number;
    discoveryDailyCards: number;
    fairUse: FairUseLimits;
  };

  creditCosts: CreditCosts;
  creditPacks: CreditPack[];

  connection: {
    /** Pending requests expire after this many days. */
    pendingExpiryDays: number;
    /** A declined pair cannot be re-requested for this many days. */
    declinedCooldownDays: number;
    /** Passed profiles reappear in discovery after this many days. */
    passReappearDays: number;
    /** Withdrawn requests can be re-sent after this many days. */
    withdrawnCooldownDays: number;
  };

  /** Discovery ranking is NEVER compatibility-only. Weights sum ~1. */
  discoveryWeights: DiscoveryWeights;

  /**
   * Accounts allowed to self-grant entitlements without a Play purchase, for
   * testing on real devices before billing is wired up. Exact email matches,
   * checked SERVER-side against the verified auth token — a client flag alone
   * can never unlock anything. Empty in production.
   */
  devUnlockEmails: string[];

  /**
   * Product AI tiers → provider model ids. Raw model names never surface in
   * UI (labels: Ask Astrally / Go Deeper / Master Reading).
   */
  aiModels: {
    standard: { provider: 'claude' | 'sarvam'; model: string };
    deeper: { provider: 'claude'; model: string };
    master: { provider: 'claude'; model: string };
  };
}

export const DEFAULT_APP_CONFIG: AppConfigValues = {
  configVersion: 'config-v2',
  scoringVersion: 'score-v1',
  /**
   * v3 = kundli became a top-level dimension carrying the dominant weight,
   * region-adjusted (Ashtakoota or Porutham) by the viewer. Every cached
   * `compatibility/{pairId}` document from v2 holds a different set of
   * dimensions and a differently-weighted overall, so the bump is what retires
   * them: compatibilityFor() recomputes on any scoreVersion mismatch.
   *
   * `scoringVersion` deliberately stays at v1 — pairScore/userScores run a
   * separate formula that this change did not touch, and bumping it would
   * invalidate every stored score document for nothing.
   */
  compatibilityVersion: 'compatibility-v3',
  gradeThresholds: { A: 0.8, B: 0.65, C: 0.5 },

  base: {
    productId: 'astrally_base_unlock',
    priceDisplay: { inr: '₹49', usd: '$0.99' },
    lifetimeOutgoingRequests: 5,
    introductoryCredits: 25,
    discoveryDailyCards: 10,
    aiTrialMessagesPerDay: 10,
  },

  pro: {
    products: { monthly: 'astrally_pro_monthly', annual: 'astrally_pro_annual' },
    // ₹29/month, ₹299/year. The annual is ~10.3 monthly-months — a real but
    // modest discount, priced for volume in an India-first market where ₹29
    // sits under the everyday-UPI-payment threshold. USD holds roughly the
    // same feel at the nearest store tiers; INR is the real price and USD
    // follows it.
    //
    // Yes, monthly Pro (₹29) is CHEAPER than the one-time Base (₹49). That is
    // not a mistake and the pricing page must never blur it: Base is bought
    // once and lasts forever; Pro is a recurring add-on ON TOP of Base. Copy
    // that says "from ₹29" without the word "month" would be lying by
    // omission — the comparison table in ProScreen spells both shapes out.
    //
    // Changing these numbers here changes DISPLAY everywhere (nothing
    // hardcodes them), but the amounts actually charged live in Play Console
    // against the product ids above — update both or the receipt contradicts
    // the screen.
    priceDisplay: { monthlyInr: '₹29', annualInr: '₹299', monthlyUsd: '$0.99', annualUsd: '$4.99' },
    dailyOutgoingRequests: 20,
    discoveryDailyCards: 50,
    fairUse: {
      messagesPerTenMinutes: 6,
      messagesPerHour: 20,
      dailyAbuseThreshold: 150,
    },
  },

  creditCosts: {
    deeperAnswer: 5,
    deeperReport: 15,
    masterReading: 60,
    regeneration: 8,
  },
  creditPacks: [
    { id: 'credits_50', name: 'Starter', credits: 50, price: { inr: '₹79', usd: '$1.49' } },
    { id: 'credits_150', name: 'Explorer', credits: 150, price: { inr: '₹199', usd: '$2.99' } },
    { id: 'credits_500', name: 'Seeker', credits: 500, price: { inr: '₹549', usd: '$7.99' } },
  ],

  connection: {
    pendingExpiryDays: 14,
    declinedCooldownDays: 30,
    passReappearDays: 14,
    withdrawnCooldownDays: 3,
  },

  discoveryWeights: {
    compatibility: 0.4,
    intentMatch: 0.15,
    geo: 0.1,
    activity: 0.12,
    profileQuality: 0.13,
    verification: 0.05,
    proBoost: 0.05,
  },

  devUnlockEmails: [],

  aiModels: {
    standard: { provider: 'claude', model: 'claude-haiku-4-5-20251001' },
    deeper: { provider: 'claude', model: 'claude-sonnet-5' },
    master: { provider: 'claude', model: 'claude-opus-5' },
  },
};

/**
 * Merge a partial remote override (Firestore `config/app`) over defaults.
 * Shallow-merges each top-level section so partial overrides work.
 */
export function mergeAppConfig(remote: Partial<AppConfigValues> | undefined): AppConfigValues {
  if (!remote) return DEFAULT_APP_CONFIG;
  const d = DEFAULT_APP_CONFIG;
  return {
    ...d,
    ...remote,
    gradeThresholds: { ...d.gradeThresholds, ...remote.gradeThresholds },
    base: { ...d.base, ...remote.base },
    pro: {
      ...d.pro,
      ...remote.pro,
      products: { ...d.pro.products, ...remote.pro?.products },
      priceDisplay: { ...d.pro.priceDisplay, ...remote.pro?.priceDisplay },
      fairUse: { ...d.pro.fairUse, ...remote.pro?.fairUse },
    },
    creditCosts: { ...d.creditCosts, ...remote.creditCosts },
    creditPacks: remote.creditPacks ?? d.creditPacks,
    devUnlockEmails: remote.devUnlockEmails ?? d.devUnlockEmails,
    connection: { ...d.connection, ...remote.connection },
    discoveryWeights: { ...d.discoveryWeights, ...remote.discoveryWeights },
    aiModels: {
      standard: { ...d.aiModels.standard, ...remote.aiModels?.standard },
      deeper: { ...d.aiModels.deeper, ...remote.aiModels?.deeper },
      master: { ...d.aiModels.master, ...remote.aiModels?.master },
    },
  };
}

/**
 * Legacy alias kept for the matching engine's grade conversion; everything
 * else should read DEFAULT_APP_CONFIG / merged remote config.
 */
export const ENTITLEMENTS = {
  scoringVersion: DEFAULT_APP_CONFIG.scoringVersion,
  gradeThresholds: DEFAULT_APP_CONFIG.gradeThresholds,
} as const;
